<template>
    <div class="checkout-ferry-form">
        <FormV2
            :id="formId"
            :validationSchema="validationSchema"
            :onValidChange="handleValidChange"
            :onChange="handleFormChange">
            <div v-if="product.Pax.length" class="checkout-ferry-form-pax">
                <Pax
                    v-for="(pax, index) in product.Pax"
                    :key="index"
                    :index="index"
                    :pax="pax"
                    :product="product"
                    :ageRangeDefinitions="ageRangeDefinitions"
                    :toDate="toDate" />
            </div>
            <div v-if="hasVehicles" class="checkout-ferry-form-content">
                <div class="checkout-ferry-form-header">
                    <span class="checkout-ferry-form-header-title">
                        <Translation
                            element="strong"
                            :translate="{
                                key: 'shared.vehicle',
                            }" />
                    </span>
                </div>
                <div class="checkout-ferry-form-content-vehicles">
                    <template v-for="(leg, legIndex) in product.Legs" :key="legIndex">
                        <Vehicles
                            :leg="leg"
                            :legIndex="legIndex"
                            :showTitle="isRoundTrip"
                            :isOutbound="legIndex == 0" />
                    </template>
                </div>
            </div>
        </FormV2>

        <Copy
            :productId="product.Id"
            :formId="formId"
            :formValid="isValid"
            :formValues="formValues" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { FerryProduct } from '@/api/Cart/cart.interfaces';
    import { useStore } from '@/store';
    import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
    import { getProductPaxFields } from '@/components/checkoutPageV3/Sections/Products/Ferry/Form/FerryPax.helpers';
    import { getVehicleFields } from '@/components/checkoutPageV3/Sections/Products/Ferry/Form/FerryVehicle.helpers';
    import { getProductToDate } from '@/api/Cart/cart.helpers';
    import { CheckoutStepState } from '@/store/checkout/types';
    import { FormContext } from 'vee-validate';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import Pax from './Pax/Pax.vue';
    import Vehicles from './Vehicles/Vehicles.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import Copy from '@/components/checkoutPageV3/Shared/Copy/Copy.vue';

    export default defineComponent({
        name: 'FerryForm',
        methods: { t },
        components: { Copy, Translation, Vehicles, FormV2, Pax },
        props: {
            sectionId: { type: String, required: true },
            product: {
                type: Object as PropType<FerryProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();
            const formId = `form_ferry_${props.product.Id}`;
            const formValues = ref<FormValues | null>(null);

            const states = computed<CheckoutStepState[]>(
                () => store.getters['checkout/getStates']
            );
            const isValid = computed<boolean>(() => {
                const step = states.value.find((x) => x.id === props.sectionId);
                return step ? step.valid : false;
            });

            const ageRangeDefinitions = computed<AgeRangeDefinitions>(
                () => store.getters['content/getCheckoutAgeRangeDefinitions']
            );

            const toDate = computed(() => getProductToDate(props.product) ?? new Date());

            const isRoundTrip = computed(() => props.product.Legs?.length > 1);

            const paxFields = computed(() => {
                return getProductPaxFields(props.product, ageRangeDefinitions.value);
            });

            const vehicleFields = computed(() => getVehicleFields(props.product));

            const hasVehicles = computed(() => vehicleFields.value.length > 0);

            const vehiclesTooltip = computed(
                () =>
                    t({
                        key: 'checkout.registration_number_required_message',
                        suffix: ' ',
                    }) +
                    t({
                        key: 'checkout.license_plate_regex_message',
                    })
            );

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            const validationSchema = computed((): ValidationSchema => {
                const schema: ValidationSchema = {};

                paxFields.value.forEach((field) => {
                    schema[field.name] = field.validation;
                });

                vehicleFields.value.forEach((field) => {
                    schema[field.name] = field.validation;
                });

                return schema;
            });

            function handleFormChange({ values }: FormContext) {
                formValues.value = values;
            }

            return {
                formId,
                validationSchema,
                ageRangeDefinitions,
                toDate,
                isRoundTrip,
                hasVehicles,
                vehiclesTooltip,
                handleValidChange,
                handleFormChange,
                isValid,
                formValues,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Form.scss';
</style>
